import React, { useMemo, useState } from 'react';
import { Button } from '@consta/uikit/Button';
import { Trans, useTranslation } from 'react-i18next';
import { Text } from '@consta/uikit/Text';
import cln from 'classnames';
import { Flex } from '##/components/Layout/Flex';
import { IMfaConnectModalConnectionStepProps } from '../../types';
import { cnMfaConnectModal } from '../../../MfaConnectModal';

import Android from './static/android.svg';
import Ios from './static/ios.svg';
import Macos from './static/macos.svg';
import Windows from './static/windows.svg';
import Alert from './static/alert.svg';

interface IDevice {
  name: string;
  appLink: string;
  faqLink: string;
  icon: unknown;
  title: string;
}

export const TOTPConnectDevice = (
  props: IMfaConnectModalConnectionStepProps,
) => {
  const { setFormStep } = props;
  const [selectedDevice, setSelectedDevice] = useState<IDevice | null>(null);
  const { t } = useTranslation();

  const handleClose = () => {
    setFormStep('type');
  };

  const handleContinue = (e: React.MouseEvent) => {
    setFormStep('qr');
  };

  const devices = useMemo<IDevice[]>(
    () => [
      {
        name: 'Android',
        appLink:
          'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=ru&pli=1',
        faqLink:
          'https://docs.google.com/document/d/1qU69p1P8upEqGxmolb9klQbMSvefNyshVP44Uc-lgXc/edit?usp=sharing',
        icon: <Android />,
        title: t('mfa.connection.device.instruction.download_from', {
          app: 'Google Play',
        }),
      },
      {
        name: 'IOS',
        appLink:
          'https://apps.apple.com/ru/app/google-authenticator/id388497605',
        faqLink:
          'https://docs.google.com/document/d/1cgGSY6uW7-GqVLmqGjc2MhoyYxgqnkK2hxWD56WpdDM/edit?usp=sharing',
        icon: <Ios />,
        title: t('mfa.connection.device.instruction.download_from', {
          app: 'App Store',
        }),
      },
      {
        name: 'Windows',
        appLink: 'https://authman.simular.co/',
        faqLink:
          'https://docs.google.com/document/d/1PYVsBqYSd3uW2xWn56Cy1yp4Z1a8kxZ3fnLYAuLsOJM/edit?usp=sharing',
        icon: <Windows />,
        title: t('mfa.connection.device.instruction.download_on'),
      },
      {
        name: 'MacOS',
        appLink: 'https://authman.simular.co/',
        faqLink:
          'https://docs.google.com/document/d/1WKFvnYDqj1Z0Pgb7yFysbMqPRJ9xVUhEQLO8IllCaRM/edit?usp=sharing',
        icon: <Macos />,
        title: t('mfa.connection.device.instruction.download_on'),
      },
    ],
    [],
  );

  return (
    <>
      <Text
        as="h1"
        size="3xl"
        weight="semibold"
        align="center"
        className={cnMfaConnectModal('Title')}
      >
        {t('mfa.connection.device.title')}
      </Text>
      <Text
        as="h3"
        size="l"
        view="ghost"
        align="center"
        weight="regular"
        className={cnMfaConnectModal('Description')}
      >
        {t('mfa.connection.device.desc')}
      </Text>
      <div className={cnMfaConnectModal('Devices')}>
        {devices.map((device) => (
          <Flex
            gap="13px"
            className={cnMfaConnectModal('Card')}
            align="center"
            key={device.name}
          >
            <div className={cnMfaConnectModal('Devices__icon')}>
              {device.icon}
            </div>
            <div className={cnMfaConnectModal('Devices__name')}>
              <Text size="m" weight="semibold">
                {device.name}
              </Text>
            </div>
            <button
              className={cln(cnMfaConnectModal('Devices__radio'), {
                [cnMfaConnectModal('Devices__radio--active')]:
                  selectedDevice?.name === device.name,
              })}
              type="button"
              onClick={() => setSelectedDevice(device)}
            />
          </Flex>
        ))}
      </div>
      {selectedDevice && (
        <Flex gap="12px" className={cnMfaConnectModal('Card')}>
          <Alert />
          <div>
            <Text size="m" weight="semibold">
              {selectedDevice.title}
            </Text>
            <Text size="s" view="ghost">
              <Trans
                components={{
                  linkApp: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      target="_blank"
                      href={selectedDevice.appLink}
                      rel="noreferrer"
                    />
                  ),
                  linkFaq: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      target="_blank"
                      href={selectedDevice.faqLink}
                      rel="noreferrer"
                    />
                  ),
                }}
              >
                {t('mfa.connection.device.instruction.desc')}
              </Trans>
            </Text>
          </div>
        </Flex>
      )}
      <Flex gap="12px" className={cnMfaConnectModal('Buttons')}>
        <Button
          onClick={handleClose}
          label={t('mfa.connection.cancel')!}
          view="secondary"
        />
        <Button
          onClick={handleContinue}
          label={t('mfa.connection.continue')!}
        />
      </Flex>
    </>
  );
};
